import ShopSwitchView from '@frontend/features/shop/components/ShopSwitchView'
import { useUserStore } from '@frontend/libs/stores/useUserStore'
import { Sort } from '@frontend/libs/types/product'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button, type Selection } from '@nextui-org/react'
import { __ } from '@wordpress/i18n'
import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

import useProductListStore from '../libs/useProductListStore'
import { setFilterToSearchParams } from '../ProductList'
import DropdownFilter from './DropdownFilter'
import DropdownSorting from './DropdownSorting'

export interface ProductListFilterProps {
  showBrandFilter?: boolean
  showCategoryFilter?: boolean
  showCollectionFilter?: boolean
  showColorFilter?: boolean
  showDiscountFilter?: boolean
  showSizeFilter?: boolean
}

export function ProductListFilter({
  showBrandFilter = true,
  showCategoryFilter = true,
  showCollectionFilter = true,
  showColorFilter = true,
  showDiscountFilter = true,
  showSizeFilter = true,
}: Readonly<ProductListFilterProps>) {
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    clearFilter,
    filters,
    init,
    pagination,
    selectedFilters,
    setSelectedFilters,
  } = useProductListStore()

  const handleOnChange = useCallback(
    (value: string[], key: string) => {
      setSelectedFilters({
        ...selectedFilters,
        [key]: value,
      })
    },
    [selectedFilters, setSelectedFilters],
  )

  const handleApplyFilter = useCallback(() => {
    init()
    setSearchParams(setFilterToSearchParams(selectedFilters))
  }, [init, selectedFilters, setSearchParams])

  const handleClearAllFilters = useCallback(() => {
    clearFilter()
    const params = new URLSearchParams()
    params.set('sort', Sort.Default)
    setSearchParams(params)
  }, [clearFilter, setSearchParams])

  const handleOnSorting = useCallback(
    (keys: Selection) => {
      init()
      if (keys !== 'all') {
        setSearchParams(
          setFilterToSearchParams({
            ...selectedFilters,
            sort: keys?.values().next().value,
          }),
        )
      }
    },
    [init, selectedFilters, setSearchParams],
  )

  const { setShopGridView } = useUserStore((state) => state)

  const handleOnChangeView = useCallback(
    (value: 'four-col' | 'three-col') => {
      setShopGridView(value)
      setSearchParams(setFilterToSearchParams(selectedFilters))
    },
    [selectedFilters, setSearchParams, setShopGridView],
  )

  const parsedFilters = filters?.filter((filter) => {
    if (filter.slug === 'brands' && showBrandFilter === false) {
      return false
    }

    if (filter.slug === 'discounts' && showDiscountFilter === false) {
      return false
    }

    if (filter.slug === 'colors' && showColorFilter === false) {
      return false
    }

    if (filter.slug === 'sizes' && showSizeFilter === false) {
      return false
    }

    if (filter.slug === 'categories' && showCategoryFilter === false) {
      return false
    }

    if (filter.slug === 'collections' && showCollectionFilter === false) {
      return false
    }

    return true
  })

  return (
    <div className="mb-10 flex justify-between">
      <div className="flex items-center divide-x [&>*]:px-4">
        {parsedFilters?.map((filter) => (
          <DropdownFilter
            key={filter.slug}
            label={filter.title}
            onChange={(value) => handleOnChange(value, filter.slug)}
            onSubmit={handleApplyFilter}
            options={filter.terms}
            value={
              selectedFilters[
                filter.slug as
                  | 'brands'
                  | 'categories'
                  | 'collections'
                  | 'colors'
                  | 'discounts'
                  | 'sizes'
              ] ?? []
            }
          />
        ))}
        {(selectedFilters.brands.length > 0 ||
          selectedFilters.categories.length > 0 ||
          selectedFilters.colors.length > 0 ||
          selectedFilters.sizes.length > 0) && (
          <Button onClick={handleClearAllFilters} variant="light">
            <XMarkIcon className="h-5 w-5" />
            {__('Clear All', 'asavagroup')}
          </Button>
        )}
      </div>
      <div className="flex items-center divide-x [&>*]:px-4">
        <div className="text-sm uppercase">
          {pagination.total} <span>{__('results', 'asavagroup')}</span>
        </div>
        <div>
          <ShopSwitchView onChange={handleOnChangeView} />
        </div>
        <div>
          <DropdownSorting
            onChange={handleOnSorting}
            value={new Set([selectedFilters.sort])}
          />
        </div>
      </div>
    </div>
  )
}

export default ProductListFilter
